import React, { useCallback, useContext, useMemo } from 'react'
import moment from 'moment'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { hasIncludedGuestsExceeded } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import GeoContext from 'contexts/geoContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import TextButton from 'components/Luxkit/Button/TextButton'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceStackLoyaltyProminentPrice from 'components/Luxkit/PricePoints/Loyalty/PriceStackLoyaltyProminentPrice'
import LuxuryPlusPriceGuard from 'loyalty/components/LuxuryPlusPriceGuard'
import { max } from 'lib/array/arrayUtils'

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  isLoggedIn: boolean;
  checkIn: moment.Moment;
  fetching?: boolean;
  currency: string;
  addAllFlights?: boolean;
  buyNowBookLater?: boolean;
  isSelected: boolean;
  room: App.RoomOccupants;
  onSelect: (packageOption: App.Package) => void;
  defaultDepositAmountPercentage: number;
}

function MultipleRoomsBookingModalPriceDetails(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    fetching,
    currency,
    buyNowBookLater,
    isSelected,
    room,
    onSelect,
    defaultDepositAmountPercentage,
  } = props

  const {
    package: pkg,
    hotelValue,
    hotelDiscountPercent,
    bundledDiscountPercent,
    hotelPrice,
    hotelMemberPrice,
    baseMemberPrice,
    flightPrice,
    taxesAndFees,
    propertyFees = 0,
  } = packageView

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { isTaxExclusiveRegion } = useContext(GeoContext)
  const showIncludedGuestsExceeded = useMemo(() => !!room && hasIncludedGuestsExceeded(room, pkg, offer) && !!pkg.roomPolicyDescription, [pkg, room, offer])

  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showMemberPrice = baseMemberPrice > 0
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const highestDiscountPercent = max([hotelDiscountPercent, bundledDiscountPercent])

  const totalPrice = isTaxExclusiveRegion ? hotelPrice : hotelPrice + (propertyFees ?? 0)
  const totalMemberPrice = isTaxExclusiveRegion ? hotelMemberPrice : hotelMemberPrice + propertyFees

  const shouldShowDiscount = (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && !offer.isDiscountPillHidden
  const hotelPriceOrHotelMemberPrice = showMemberPrice ? hotelMemberPrice : hotelPrice
  const shouldShowValue = hotelValue > hotelPriceOrHotelMemberPrice && pkg.shouldDisplayValue
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkIn, hotelPriceOrHotelMemberPrice) && !buyNowBookLater

  const onSelectRoom = useCallback(() => onSelect(pkg), [onSelect, pkg])

  return (
    <Group direction="vertical" gap={12}>
      <Group direction="vertical" gap={8}>
        {isDepositEnabled && <div>
          <SecureWithDepositTagAndModal offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds} />
        </div>}
        <div>
          <PriceRowPriceCaption>
            {pluralizeToString('night', pkg.duration)}
          </PriceRowPriceCaption>
          <LuxuryPlusPriceGuard
            showMemberPrice={showMemberPrice}
            fallbackPrice={ <PriceRowPrice
              size="S"
              price={totalPrice}
              saleUnit="total"
              taxesAndFees={taxesAndFees}
            />}
          >
            <PriceStackLoyaltyProminentPrice
              size="S"
              memberPrice={totalMemberPrice}
              saleUnit="total"
              nonMemberPrice={totalPrice}
              taxesAndFees={taxesAndFees}
              horizontalAlign="start"
              orientation="vertical"
            />
          </LuxuryPlusPriceGuard>
          <PriceRowTaxesAndFees
            isTaxExclusiveRegion={offer.property?.taxesPayableAtProperty}
            taxesAndFees={taxesAndFees}
          />
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="M"
            value={offer.bundledWithFlightsOnly ? hotelValue + flightPrice : hotelValue}
            discountPercentage={shouldShowDiscount ? highestDiscountPercent : undefined}
          />}
          {showIncludedGuestsExceeded && (
            <AdditionalGuestsPopup
              complex={false}
              description="+ Extra guest surcharge"
              modalContent={pkg.roomPolicyDescription}
            />
          )}

          <LoadingIndicator visible={fetching} floating opaque />
        </div>
      </Group>

      <TextButton kind={isSelected ? 'primary' : 'secondary'} disabled={isSelected} onClick={onSelectRoom}>
        {isSelected ? 'Selected' : 'Select'}
      </TextButton>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  offer: state.offer,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(MultipleRoomsBookingModalPriceDetails)
